import React, { useEffect, useState } from 'react';

import classNames from 'classnames';

import { actualDate, cashflowBalanced, cashflowTotal, defaultCashflow } from './sendForCommitmentHelpers';

import { formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import DatePickerField from 'CommonComponents/DatePickerField';
import Modal from 'CommonComponents/Modal';
import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';
import TextButton from 'CommonComponents/TextButton';

import TrashIcon from 'Assets/icons/trash.svg';

/**
 * How one row's commitment is paid out over time.
 *
 * The entries must add up to the commitment exactly. The server stores each as a
 * percentage of the total, so a set that sums to anything else cannot be
 * represented at all — hence Apply is disabled rather than merely warned about,
 * and the shortfall or excess is named while it is.
 */
export default function SendForCommitmentCashflowModal({ modal, row, onApply }) {
    const [entries, setEntries] = useState([]);

    // Re-seeded whenever the pop-up is pointed at a different row: one modal
    // instance serves the whole table, so without this the previous row's entries
    // would be waiting in it.
    useEffect(() => {
        if (!row) {
            return;
        }

        // Seeded against the COMMIT VALUE, not the actual: a row committing more than
        // was spent opens with the balance already filled in, which is the whole of
        // what the reconciler is being asked to look at.
        setEntries(row.state.cashflowAdjusted ? row.state.cashflow.map((entry) => ({ ...entry })) : defaultCashflow(row.item, row.state.committed));
    }, [row?.item?.id, row?.state?.cashflowAdjusted]);

    if (!row) {
        return <Modal id="send-for-commitment-cashflow-modal" modal={modal} />;
    }

    const { item } = row;
    // Balanced against what the row actually commits, which is the figure the
    // server converts to percentages — not the raw actual. They are equal on any
    // row that can be submitted, and where they are not, this is the honest one.
    const target = row.state.committed;
    const total = cashflowTotal(entries);
    const balanced = cashflowBalanced(entries, target);
    const variance = total - target;

    const close = () => modal.hide();

    const setEntry = (index, field, value) => {
        setEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)));
    };

    const addEntry = () => {
        // Dated from the actual like the first one, and left at zero: the amount is
        // the whole point of adding a row, and pre-filling it would mean guessing
        // how the reconciler wants to split what is already allocated.
        setEntries((prev) => [...prev, { date: actualDate(item), amount: 0 }]);
    };

    const removeEntry = (index) => {
        setEntries((prev) => prev.filter((_, i) => i !== index));
    };

    return (
        <Modal id="send-for-commitment-cashflow-modal" modal={modal}>
            <ModalHeader close={close} subtitle={`${item.supplier || ''} · ${item.budget_line || ''} · ${formatToFixedMoneyWithLocale(target, 2)}`}>
                {$t('Adjust cashflow')}
            </ModalHeader>

            <div className="tw-mb-6">
                <div className="tw-flex tw-flex-col tw-gap-y-2">
                    {entries.map((entry, index) => (
                        <div key={index} className="tw-flex tw-items-center tw-gap-x-2">
                            <DatePickerField
                                name={`cashflow-date-${index}`}
                                value={entry.date || null}
                                valueFormat="yyyy-MM-dd"
                                dateFormat="dd/MM/yyyy"
                                className={classNames('input text-input !tw-w-36', entry.locked ? 'input--disabled' : '!tw-bg-white')}
                                disabled={!!entry.locked}
                                onChange={(name, value) => setEntry(index, 'date', value)}
                            />
                            <input
                                type="number"
                                step="any"
                                className={classNames('input text-input !tw-w-36 tw-text-end', entry.locked ? 'input--disabled' : '!tw-bg-white')}
                                value={entry.amount}
                                // The transaction's own payment: what it was and when it went out
                                // is a matter of record, not something this screen may restate.
                                // Only the balance on top of it is the reconciler's to place.
                                disabled={!!entry.locked}
                                aria-label={$t('Amount')}
                                onChange={(e) => setEntry(index, 'amount', e.target.value)}
                            />
                            <TextButton
                                onClick={() => removeEntry(index)}
                                disabled={entries.length === 1 || !!entry.locked}
                                aria-label={$t('Remove entry')}
                                className="tw-flex tw-items-center"
                            >
                                <TrashIcon className="tw-h-4 tw-w-4" />
                            </TextButton>
                        </div>
                    ))}
                </div>

                <div className="tw-mt-2">
                    <TextButton onClick={addEntry}>{$t('Add entry')}</TextButton>
                </div>

                <div className="tw-mt-4 tw-flex tw-items-baseline tw-justify-between tw-border-t tw-border-grey tw-pt-3 tw-text-sm">
                    <span className="tw-font-medium tw-text-charcoal">{$t('Total')}</span>
                    <span className={classNames('tw-font-semibold', balanced ? 'tw-text-green' : 'tw-text-red')}>
                        {formatToFixedMoneyWithLocale(total, 2)}
                        {balanced
                            ? ` · ${$t('matches the committed value')}`
                            : ` · ${
                                  variance > 0
                                      ? $t('{{amount}} over', { amount: formatToFixedMoneyWithLocale(variance, 2) })
                                      : $t('{{amount}} short', { amount: formatToFixedMoneyWithLocale(Math.abs(variance), 2) })
                              }`}
                    </span>
                </div>

                <p className="tw-mt-2 tw-mb-0 tw-text-xs tw-text-charcoal-50">
                    {$t('The entries must add up to the value being committed. Apply stays disabled until they do.')}
                </p>
            </div>

            <ModalFooter
                submitButtonVariant="primary"
                cancelButtonText={$t('Cancel')}
                primaryButtonText={$t('Apply')}
                primaryButtonDisabled={!balanced}
                onCancel={close}
                handleSubmit={() => {
                    onApply(
                        item.id,
                        // `locked` is carried through rather than dropped: reopening an
                        // already-adjusted row restores from this, and without the flag the
                        // actual's own entry would come back editable.
                        entries.map((entry) => ({ date: entry.date, amount: Number(entry.amount), ...(entry.locked ? { locked: true } : {}) })),
                    );
                    modal.hide();
                }}
            />
        </Modal>
    );
}
